import React, { useEffect, useState } from 'react';
import Confetti from 'react-confetti';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../firebase';
import { getFirestore, doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';

const firestore = getFirestore();

const ConfettiCelebration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [redirectRoute, setRedirectRoute] = useState('/userdash'); // Default fallback


  useEffect(() => {
    // Smart routing logic
    const determineCorrectDashboard = async () => {
      try {
        // Option 1: Check if route was passed via navigation state
        if (location.state?.redirectTo) {
          console.log("📍 Using passed redirect route:", location.state.redirectTo);
          setRedirectRoute(location.state.redirectTo);
          return;
        }

        // Option 2: Check localStorage for recent registration
        const storedUserType = localStorage.getItem('userType');
        if (storedUserType) {
          console.log("📍 Found userType in localStorage:", storedUserType);
          
          switch (storedUserType) {
            case 'investment':
              setRedirectRoute('/investdashboard');
              break;
            case 'training':
              setRedirectRoute('/academy/dashboard');
              break;
            case 'general':
              setRedirectRoute('/userdash');
              break;
            default:
              setRedirectRoute('/userdash');
          }
          return;
        }

        // Option 3: Check Firebase Auth user and query collections
        const currentUser = auth.currentUser;
        if (currentUser) {
          console.log("📍 Checking Firestore for user type...");
          const userEmail = currentUser.email;
          
          // Check investment users
          const investmentQuery = query(
            collection(firestore, "UserRegistrations"), 
            where("email", "==", userEmail)
          );
          const investmentSnapshot = await getDocs(investmentQuery);
          
          if (!investmentSnapshot.empty) {
            console.log("📍 Found investment user");
            setRedirectRoute('/investdashboard');
            return;
          }

          // Check training users
          const trainingQuery = query(
            collection(firestore, "FormRegistration"), 
            where("emailAddress", "==", userEmail)
          );
          const trainingSnapshot = await getDocs(trainingQuery);
          
          if (!trainingSnapshot.empty) {
          console.log("📍 Found training user");
            setRedirectRoute('/academy/dashboard');
            return;
          }

          // Check general investors
          const investorDoc = doc(firestore, "Investors", currentUser.uid);
          const investorSnap = await getDoc(investorDoc);
          
          if (investorSnap.exists()) {
            console.log("📍 Found general investor");
            setRedirectRoute('/userdash');
            return;
          }
        }

        // Fallback: Default to general dashboard
        console.log("📍 Using fallback route");
        setRedirectRoute('/userdash');
        
      } catch (error) {
        console.error("Error determining dashboard:", error);
        setRedirectRoute('/userdash');
      }
    };

    determineCorrectDashboard();
  }, [location]);

  useEffect(() => {
    const timer = setTimeout(() => {
      console.log("🚀 Redirecting to:", redirectRoute);
      navigate(redirectRoute);
    }, 9000);

    return () => clearTimeout(timer);
  }, [navigate, redirectRoute]);

  // Get celebration message based on user type
  const getCelebrationMessage = () => {
    const messages = {
      'investment': {
        title: "Welcome to VON Scalper! 🎯",
        subtitle: "Your copy trading journey begins now!",
        description: "We're setting up your trading dashboard with professional strategies."
      },
      'training': {
        title: "Welcome to Our Training Program! 🎓",
        subtitle: "Your algorithmic trading education starts here!",
        description: "We're preparing your learning materials and course access."
      },
      'general': {
        title: "Welcome to KS Quantitative! 💼",
        subtitle: "Your investment journey begins now!",
        description: "We're setting up your personalized investment dashboard."
      },
      'default': {
        title: "Thank you for joining us! 🚀",
        subtitle: "We're excited to have you here!",
        description: "We will review your application and contact you soon."
      }
    };

    const storedType = localStorage.getItem('userType') || 'default';
    return messages[storedType] || messages.default;
  };

  const message = getCelebrationMessage();

  return (
    <>
      <Confetti 
        width={window.innerWidth} 
        height={window.innerHeight}
        recycle={false}
        numberOfPieces={500}
        gravity={0.3}
      />
      <div
        style={{
          background: "rgba(255, 255, 255, 0.3)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          borderRadius: "0.5rem",
          padding: "2rem",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          width: "100%",
          maxWidth: "80%",
          margin: "0 auto",
          color: "whitesmoke",
          textAlign: "center",
          marginTop: "10vh",
        }}
      >
        <div className="mb-6">
          <h1
            style={{
              fontSize: "2.5rem",
              fontWeight: "bold",
              marginBottom: "1rem",
              background: "linear-gradient(45deg, #3b82f6, #8b5cf6)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            {message.title}
          </h1>
          
          <h2
            style={{
              fontSize: "1.5rem",
              fontWeight: "600",
              marginBottom: "1rem",
              color: "#60a5fa"
            }}
          >
            {message.subtitle}
          </h2>
          
          <p style={{ fontSize: "1.1rem", marginBottom: "2rem" }}>
            {message.description}
          </p>
        </div>

        {/* Progress indicator */}
        <div className="mb-6">
          <div className="flex justify-center items-center space-x-4 mb-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm">Registration Complete</span>
            </div>
            <div className="w-8 h-0.5 bg-blue-400"></div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
              <span className="text-sm">Setting up dashboard...</span>
            </div>
          </div>
        </div>

        {/* Redirect countdown */}
        <div 
          style={{
            background: "rgba(59, 130, 246, 0.2)",
            border: "1px solid rgba(59, 130, 246, 0.3)",
            borderRadius: "0.5rem",
            padding: "1rem",
            marginTop: "2rem"
          }}
        >
          <p style={{ fontSize: "0.9rem", color: "#93c5fd" }}>
            🚀 Redirecting to your dashboard in a few seconds...
          </p>
          <button
            onClick={() => navigate(redirectRoute)}
            style={{
              background: "linear-gradient(45deg, #3b82f6, #8b5cf6)",
              color: "white",
              border: "none",
              borderRadius: "0.5rem",
              padding: "0.5rem 1rem",
              marginTop: "1rem",
              cursor: "pointer",
              fontSize: "0.9rem",
              fontWeight: "600"
            }}
          >
            Go to Dashboard Now →
          </button>
        </div>
      </div>
    </>
  );
};

export default ConfettiCelebration;